import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AuthState, User } from '@/types/auth'
import { authService } from '@/services/authService'
import { useLessonProgressStore } from '@/store/lessonProgressStore'
import { useStudyPlanStore } from '@/store/studyPlanStore'
import { isTokenExpired } from '@/utils/tokenValidation'

interface StoreState extends Omit<AuthState, 'isAuthenticated'> {
  isAuthenticated: () => boolean
}

export const useAuthStore = create<StoreState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      registerPrefill: null,
      isAuthenticated: () => {
        const state = get()
        if (!state.token || !state.user) {
          return false
        }
        if (isTokenExpired(state.token)) {
          set({ user: null, token: null })
          return false
        }
        return true
      },

      login: async (email: string, password: string) => {
        const result = await authService.login(email, password)
        set({ user: result.user, token: result.token })
      },

      register: async (data: { email: string; password: string; name: string; role: 'student' | 'parent'; ref?: string; adultConfirmed?: boolean }) => {
        const result = await authService.register(data)
        set({ user: result.user, token: result.token, registerPrefill: result.prefill ?? null })
      },

      logout: async () => {
        await authService.logout()
        const progressStore = useLessonProgressStore.getState()
        progressStore.clearPendingUpdates()
        progressStore.clearCache()
        useStudyPlanStore.getState().reset()
        set({ user: null, token: null, registerPrefill: null })
      },

      updateUser: (user: User) => set({ user }),

      setToken: (token: string) => set({ token }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, token: state.token }),
    }
  )
)

export const useUser = () => useAuthStore((state) => state.user)
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated())
export const useToken = () => useAuthStore((state) => state.token)
